<template>
    <article
        class="border rounded relative bg-white mb-4 shadow flex flex-col duration-300 transition-all overflow-hidden"
        :class="{
            'border-gray-200': !article.is_pinned,
            'border-yellow-500': article.is_pinned,
            'flex-col md:flex-row': fullWidth,
            'group hover:border-primary hover:shadow-xl': type !== 'div',
        }"
    >
        <div v-if="publishedToday"
             class="absolute z-20 left-0 top-0 py-1 px-8 bg-primary text-white text-xs shadow-xl -rotate-45 origin-top-right -translate-x-1/2 -translate-y-full">
            LATEST
        </div>

        <component :is="componentType"
            :href="href ? href : route('articles.show', {article: article.slug})"
            class="relative flex flex-col items-start py-3 px-4 duration-300 transition-colors flex-1 w-full"
            :class="{
                'flex-col md:flex-row': fullWidth
            }"
        >
            <figure
                class="rounded overflow-hidden m-0 flex-none relative w-full"
                :class="{
                    'md:w-36': fullWidth,
                }"
            >
                <div
                    class="absolute left-0 right-0 top-0 bottom-0 bg-black bg-opacity-50 z-10 flex justify-center
                        items-center text-white duration-500 transition-all opacity-0 group-hover:opacity-100"
                >
                    <span class="transform duration-500 -translate-y-20 group-hover:translate-y-0 px-3 py-1 rounded bg-primary text-sm">
                        <slot name="read-more-label">
                            READ MORE
                        </slot>
                    </span>
                </div>
                <img :src="article.cover_image_url" class="block w-full transform group-hover:scale-125 duration-500" :alt="article.title" />
            </figure>
            <div
                class="w-full mt-2 ml-0 flex-1 flex flex-col"
                :class="{
                    'md:ml-3': fullWidth,
                }"
            >
                <div
                    class="w-full justify-between items-start mb-2"
                    :class="{
                        'md:flex': fullWidth,
                    }"
                >
                    <div
                        class="text-sm text-gray-500 w-full mb-2"
                        :class="{
                            'lg:flex lg:space-x-2': fullWidth,
                        }"
                    >
                        <div class="font-bold">
                            {{ article.presentable.published_at }}
                        </div>
                        <div v-if="article.pending_publish" class="flex items-center space-x-1">
                            <Tooltip>
                                <template #trigger>
                                    <ClockIcon class="w-4 text-yellow-500" />

                                </template>
                                Will be published automatically
                            </Tooltip>
                        </div>
                        <div class="border-r border-gray-200 mx-2" />
                        <div class="opacity-70">{{ article.category.title }}</div>
                    </div>
                    <div
                        class="flex-initial flex text-xs text-gray-400 divide-x divide-gray-300 items-start"
                        :class="{
                            'md:justify-end md:mt-0': fullWidth,
                        }"
                    >
                        <div
                            class="px-2 whitespace-nowrap flex space-x-2 pl-0"
                            :class="{
                                'md:pl-2': fullWidth,
                            }"
                        >
                            <span class="text-primary font-bold">{{ article.images_count }}</span>
                            <span class="flex space-x-1">
                                <PhotographIcon class="w-4 h-full" />
                                <span
                                    class="hidden"
                                    :class="{
                                        'lg:block': fullWidth,
                                    }"
                                >IMAGES</span>
                            </span>
                        </div>
                        <div class="px-2 whitespace-nowrap flex space-x-2">
                            <span class="text-primary font-bold">{{ article.videos_count }}</span>
                            <span class="flex space-x-1">
                                <VideoCameraIcon class="w-4 h-full" />
                                <span
                                    class="hidden"
                                    :class="{
                                        'lg:block': fullWidth,
                                    }"
                                >VIDEOS</span>
                            </span>
                        </div>
                        <div class="px-2 pr-0 whitespace-nowrap flex space-x-2">
                            <span class="text-primary font-bold">{{ article.documents_count }}</span>
                            <span class="flex space-x-1">
                                <DocumentIcon class="w-4 h-full" />
                                <span
                                    class="hidden"
                                    :class="{
                                        'lg:block': fullWidth,
                                    }"
                                >DOCUMENTS</span>
                            </span>
                        </div>
                    </div>
                </div>
                <div class="text-primary">{{ article.organisation.title }}</div>
                <div class="flex flex-col flex-1">
                    <h2 class="font-bold text-lg mb-1 flex-initial">{{ article.title }}</h2>
                    <div
                        class="relative flex-1 min-h-[2.6rem]"
                    >
                        <div class="absolute left-0 right-0 top-0 bottom-0 text-sm opacity-50 overflow-hidden" v-html="article.synopsis">
                        </div>
                        <div
                            class="absolute left-0 right-0 bottom-0 h-10 bg-gradient-to-t from-[#ffff] to-[#fff0]"
                        />
                    </div>
                </div>
            </div>

        </component>
        <div
            class="py-3 px-4 justify-between w-full bg-white z-10 relative"
            :class="{
                'md:flex md:flex-col md:border-l md:border-gray-300 md:w-40': fullWidth,
            }"
        >
            <div class="w-2/3 mx-auto" v-if="rating">
                <StarRating
                    v-if="article.average_rating > 0 || alwaysShowRating"
                    :stacked="true"
                    :max="5"
                    :count="article.ratings_count"
                    :rating="article.average_rating"
                />
            </div>
            <div
                class="mt-3 text-sm flex space-x-2 justify-center"
                :class="{
                    'md:block md:space-x-0': fullWidth,
                }"
            >
                <template v-if="$slots.buttons || buttons">
                    <slot name="buttons">
                        <div
                            class="flex space-x-2"
                            :class="{
                                'md:flex-col md:space-x-0': fullWidth,
                                'flex-row space-x-2': !fullWidth,
                            }"
                        >
                            <ToolbarButton
                                type="a"
                                :href="route('profile.switcher', [article.organisation.uuid])"
                                :single="true"
                                class="mb-2 w-full"
                            >
                                <span class="flex w-full items-center space-x-2">
                                    <GlobeAltIcon class="w-5" />
                                    <span class="hidden md:inline-block" v-if="fullWidth">
                                        Profile
                                    </span>
                                </span>
                            </ToolbarButton>
                            <ToolbarButton
                                type="link"
                                href="#"
                                :single="true"
                                class="mb-2 w-full"
                                @click.prevent="downloadPresspack"
                            >
                                <span class="flex w-full items-center space-x-2">
                                    <span class="flex-initial">
                                        <Spinner class="w-4" v-if="preparingDownload" />
                                        <ArchiveIcon class="w-4" v-else />
                                    </span>
                                    <span class="hidden md:inline-block" v-if="fullWidth">
                                        Presspack
                                    </span>
                                </span>
                            </ToolbarButton>
                            <ToolbarButton
                                type="button"
                                href="#"
                                class="mb-2 w-full"
                                :single="true"
                                @click.prevent="$emit('share', article)"
                            >
                                <span class="flex w-full items-center space-x-2">
                                    <ShareIcon class="w-4" />
                                    <span class="hidden md:inline-block" v-if="fullWidth">
                                        Share
                                    </span>
                                </span>
                            </ToolbarButton>
                        </div>
                    </slot>
                </template>
            </div>
        </div>
    </article>
</template>

<script setup>
import ToolbarButton from "@/Components/Buttons/ToolbarButton.vue";
import ArchiveIcon from "@/Icons/Small/ArchiveIcon.vue";
import ShareIcon from "@/Icons/Small/ShareIcon.vue";
import StarRating from "@/Components/StarRating.vue";
import {route} from "@/Scripts/Routing/Router.js";
import {Link, usePage} from "@inertiajs/inertia-vue3";
import {
    ClockIcon,
    DocumentIcon,
    GlobeAltIcon,
    PhotographIcon,
    VideoCameraIcon,
} from "@heroicons/vue/solid";
import Tooltip from "@/Components/Tooltip.vue";
import {computed, ref, watch} from "vue";
import {Inertia} from "@inertiajs/inertia";
import Spinner from "@/Components/Spinner.vue";

const emits = defineEmits(['share']);
const props = defineProps({
    alwaysShowRating: Boolean,
    showLatestTag: {
        type: Boolean,
        default: true,
    },
    trashed: Boolean,
    href: String,
    tags: Array,
    buttons: {
        type: Boolean,
        default: true,
    },
    fullWidth: Boolean,
    article: Object,
    type: {
        type: String,
        default: 'link',
        validator: (value) => {
            return ['link', 'button', 'a', 'div'].indexOf(value) !== -1;
        }
    },
    rating: {
        type: Boolean,
        default: true,
    },
});

const componentType = computed(() => {
    if (props.type === 'link') {
        return Link;
    }

    return props.type;
})

const publishedToday = computed(() => {
    if (! props.showLatestTag) {
        return false;
    }

    return props.article.published_today;
})

const preparingDownload = ref(false);
const downloadAvailable = ref(null);

const downloadPresspack = () => {
    preparingDownload.value = true;
    Echo.private('notifications.' + usePage().props.value.user.id).listen('.presspack.download.ready', (e) => {
        preparingDownload.value = false;
        downloadAvailable.value = e;
    })
    Inertia.post(route('presspacks.download.generate', [props.article.uuid]), {
        preserveState: true,
    });
};

watch(downloadAvailable, () => {
    if (downloadAvailable.value.action) {
        window.location.href = downloadAvailable.value.action;
    }
});

</script>

<style scoped>

</style>
